/**
 * A picture you can zoom into.
 *
 * The app blocks pinch and double-tap everywhere else, because an interface
 * you can accidentally shove out of alignment does not feel like an app. A
 * photo is the exception - it is the one thing people genuinely want to get
 * closer to - so this opts back in and handles the gestures itself rather
 * than letting the browser zoom the whole page.
 *
 * Click the picture to zoom in on the spot you clicked; click again to go
 * back. Both ease smoothly. The wheel zooms too, pinch works on a touch
 * screen, and while zoomed in you drag to look around. Clicking anywhere that
 * is not the picture asks the caller to close.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const MIN = 1;
const MAX = 5;
const CLICK_ZOOM = 2.4;
/* Movement under this many pixels is a click, not a drag. */
const SLOP = 5;

type Point = { x: number; y: number };

export function Zoomable({ src, alt, onSwipe, onBackground }: {
  src: string;
  alt: string;
  /** Called on a horizontal flick while zoomed out, for next/previous. */
  onSwipe?: (dir: -1 | 1) => void;
  /** A click on the space around the picture. */
  onBackground?: () => void;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState<Point>({ x: 0, y: 0 });
  const [loaded, setLoaded] = useState(false);
  const [live, setLive] = useState(false);

  /* Gesture bookkeeping. Refs, not state: these change every pointer move and
     re-rendering on each one would drop frames. */
  const pointers = useRef(new Map<number, Point>());
  const start = useRef<{ dist: number; scale: number; offset: Point } | null>(null);
  const drag = useRef<{ from: Point; offset: Point; moved: boolean; onImage: boolean } | null>(null);

  /* A new picture starts fresh, and fades in once it has arrived. */
  useEffect(() => { setScale(1); setOffset({ x: 0, y: 0 }); setLoaded(false); }, [src]);

  /** Keeps the picture from being dragged off the screen entirely. */
  const clamp = useCallback((next: Point, atScale: number): Point => {
    const node = wrap.current;
    if (!node) return next;
    const b = node.getBoundingClientRect();
    const room = { x: (b.width * (atScale - 1)) / 2, y: (b.height * (atScale - 1)) / 2 };
    return {
      x: Math.max(-room.x, Math.min(room.x, next.x)),
      y: Math.max(-room.y, Math.min(room.y, next.y))
    };
  }, []);

  const zoomTo = useCallback((next: number, around?: Point) => {
    const node = wrap.current;
    const to = Math.max(MIN, Math.min(MAX, next));
    if (to === 1) { setScale(1); setOffset({ x: 0, y: 0 }); return; }
    setScale((was) => {
      if (node && around) {
        /* Keep whatever is under the pointer under the pointer. */
        const b = node.getBoundingClientRect();
        const cx = around.x - (b.left + b.width / 2);
        const cy = around.y - (b.top + b.height / 2);
        setOffset((o) => clamp({
          x: o.x - ((cx - o.x) / was) * (to - was),
          y: o.y - ((cy - o.y) / was) * (to - was)
        }, to));
      }
      return to;
    });
  }, [clamp]);

  function onPointerDown(e: React.PointerEvent) {
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()] as [Point, Point];
      start.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale, offset };
      drag.current = null;
      setLive(true);
      return;
    }
    drag.current = {
      from: { x: e.clientX, y: e.clientY },
      offset,
      moved: false,
      onImage: (e.target as HTMLElement).tagName === 'IMG'
    };
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    /* Two fingers: pinch. */
    if (pointers.current.size === 2 && start.current) {
      const [a, b] = [...pointers.current.values()] as [Point, Point];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const next = Math.max(MIN, Math.min(MAX, start.current.scale * (dist / start.current.dist)));
      setScale(next);
      setOffset(clamp(start.current.offset, next));
      return;
    }

    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.from.x;
    const dy = e.clientY - d.from.y;
    if (!d.moved && Math.hypot(dx, dy) < SLOP) return;
    d.moved = true;
    /* One finger while zoomed in: pan, following the pointer with no easing. */
    if (scale > 1) {
      setLive(true);
      setOffset(clamp({ x: d.offset.x + dx, y: d.offset.y + dy }, scale));
    }
  }

  function onPointerUp(e: React.PointerEvent) {
    const was = pointers.current.get(e.pointerId);
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) start.current = null;
    const d = drag.current;
    drag.current = null;
    setLive(false);
    if (!was || !d || pointers.current.size) return;

    if (!d.moved) {
      /* A click: on the picture it zooms in there or back out; anywhere else it closes. */
      if (d.onImage) zoomTo(scale > 1 ? 1 : CLICK_ZOOM, { x: e.clientX, y: e.clientY });
      else if (scale > 1) zoomTo(1);
      else onBackground?.();
      return;
    }

    /* A flick sideways while zoomed out moves to the next picture. */
    if (scale === 1 && onSwipe) {
      const dx = e.clientX - d.from.x;
      const dy = e.clientY - d.from.y;
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) onSwipe(dx < 0 ? 1 : -1);
    }
  }

  return (
    <div
      ref={wrap}
      // The app-wide zoom block skips anything marked this way.
      data-zoomable
      className="relative flex h-full w-full touch-none items-center justify-center overflow-hidden"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onWheel={(e) => {
        if (Math.abs(e.deltaY) < 2) return;
        zoomTo(scale * (e.deltaY < 0 ? 1.18 : 1 / 1.18), { x: e.clientX, y: e.clientY });
      }}
    >
      <img
        src={src}
        alt={alt}
        draggable={false}
        onLoad={() => setLoaded(true)}
        className="max-h-full max-w-full rounded-[var(--radius-l)] object-contain shadow-[0_24px_70px_rgba(0,0,0,.6)] select-none"
        style={{
          transform: 'translate(' + offset.x + 'px,' + offset.y + 'px) scale(' + (loaded ? scale : scale * 0.96) + ')',
          opacity: loaded ? 1 : 0,
          // Eased for clicks and the wheel; none while a finger is moving it.
          transition: live ? 'opacity 260ms ease' : 'transform 380ms cubic-bezier(.2,.8,.2,1), opacity 260ms ease',
          cursor: scale > 1 ? (live ? 'grabbing' : 'zoom-out') : 'zoom-in',
          willChange: 'transform'
        }}
      />

      {/* Only while it means something. */}
      <button
        type="button"
        tabIndex={scale > 1 ? 0 : -1}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => { e.stopPropagation(); zoomTo(1); }}
        className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-white/[.14] px-3 py-1 text-[11.5px] font-bold text-white backdrop-blur-sm transition-[opacity,transform] duration-300"
        style={{ opacity: scale > 1 ? 1 : 0, transform: 'translate(-50%,' + (scale > 1 ? '0' : '6px') + ')', pointerEvents: scale > 1 ? 'auto' : 'none' }}
      >
        {Math.round(scale * 100)}% · reset
      </button>
    </div>
  );
}
